/**
 * Per-event handlers for the `/events` stream, extracted from
 * `useChatMessages` so the hook is left with subscription lifecycle +
 * the send pipeline. Each handler is a plain function over a small
 * mutable context — no React imports, so the dispatch logic is readable
 * and testable in isolation.
 *
 * Dispatch handles the BE's streaming turn shape: `text_delta` chunks
 * accumulate into one assistant bubble keyed by a synthetic id;
 * `assistant_message_persisted` swaps in the real BE-issued id and
 * clears the streaming flag; `tool_use` / `tool_result` open and flip
 * rows on a controlled `ServerToolGroup`; `user_message_persisted`
 * reconciles the optimistic user bubble with the BE-issued id (or
 * appends a peer-client bubble when no optimistic one exists); `done` /
 * `error` close the turn. The legacy `assistant_text` branch is retained
 * for backward compatibility with the ui-mock harness.
 */
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { toolDisplayName } from '@sb/remote-control/util/toolDisplayName';

import type { ChatMessage } from './types';

/**
 * A turn-terminal failure surfaced by the orchestrator's `error` event
 * (codes: token_budget, llm_unavailable, auth_failed, max_iterations,
 * response_truncated, persistence_unavailable, session_deleted,
 * confirmation_timeout). Distinct from `streamError`, which is a
 * transport-level disconnect — and which the events handler clears on
 * every incoming frame, so it can't carry a terminal error.
 */
export interface AgentChatError {
  code: string;
  message: string;
}

/** A `{toolUseID, prompt}` pair, as carried by `confirmation_request`
 * events and the `subscribed` snapshot's `pendingConfirmation`. */
export type ConfirmationPayload = { toolUseID: string; prompt: string };

/**
 * Validate a raw wire value into a confirmation payload, or null.
 * Shared by the snapshot parser and the mount's `confirmation_request`
 * handling so the two never drift.
 */
export function parseConfirmation(value: unknown): ConfirmationPayload | null {
  if (typeof value !== 'object' || value === null) return null;

  const payload = value as { toolUseID?: unknown; prompt?: unknown };

  if (
    typeof payload.toolUseID !== 'string' ||
    typeof payload.prompt !== 'string'
  ) {
    return null;
  }

  return { toolUseID: payload.toolUseID, prompt: payload.prompt };
}

/**
 * Everything a handler may touch. Refs are the hook's own — handlers
 * mutate them in place so decisions made synchronously in one event are
 * visible to the next event of the same tick, before React flushes any
 * set-state updater.
 */
export type ChatEventContext = {
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setIsThinking: (value: boolean) => void;
  /**
   * Whether a turn is in flight — raised by every turn-progress event
   * and lowered by the terminal ones. Called by the dispatcher off the
   * `TURN_PROGRESS_EVENTS` / `TURN_TERMINAL_EVENTS` classification
   * below, never from a handler body, so the invariant is one table
   * instead of a call per handler. Drives the composer's stop
   * affordance; distinct from `setIsThinking`, which is lowered during
   * visible streaming while the turn still runs.
   */
  setIsTurnActive: (value: boolean) => void;
  setAgentError: (error: AgentChatError | null) => void;
  /** Synthetic id of the assistant bubble accumulating text_delta. */
  streamingMessageIdRef: MutableRefObject<string | null>;
  /** Synthetic id of the ServerToolGroup collecting tool_use rows. */
  toolGroupMessageIdRef: MutableRefObject<string | null>;
  /** Synthetic id of the optimistic user bubble from the last send. */
  pendingUserMessageIdRef: MutableRefObject<string | null>;
  /**
   * Consume the pending lost-frames flag now that the turn is over
   * (persisted history is complete) and trigger the mount's reseed.
   */
  consumeResyncAtTurnEnd: () => void;
};

type ChatEventHandler = (
  event: Record<string, unknown>,
  ctx: ChatEventContext,
) => void;

/**
 * BE emits `user_message_persisted` (with `text`) once the sender's
 * prompt is committed to storage. Sender path: swap the optimistic
 * bubble's synthetic id for the BE-issued messageID so it aligns with
 * the seed loader and peer clients. Peer path (no pending optimistic
 * id): append a fresh user bubble with the persisted id + text. Either
 * path: skip if a bubble with `messageID` is already present (e.g. seed
 * already loaded it).
 */
const handleUserMessagePersisted: ChatEventHandler = (event, ctx) => {
  const payload = event as { messageID?: unknown; text?: unknown };

  if (typeof payload.messageID !== 'string' || !payload.messageID) {
    return;
  }

  if (typeof payload.text !== 'string') {
    // Older BE without the text payload — nothing to render peer-side.
    // Sender already has the optimistic bubble.
    return;
  }

  const { messageID, text: msgText } = payload;
  const pendingID = ctx.pendingUserMessageIdRef.current;

  ctx.setMessages((prev) => {
    if (prev.some((m) => m.id === messageID)) return prev;

    if (pendingID !== null) {
      const idx = prev.findIndex((m) => m.id === pendingID);

      if (idx >= 0) {
        const next = prev.slice();

        next[idx] = { ...next[idx], id: messageID, messageID };

        return next;
      }
    }

    return [...prev, { id: messageID, messageID, role: 'user', text: msgText }];
  });

  ctx.pendingUserMessageIdRef.current = null;
};

/**
 * BE emits `text_delta` chunks for the streaming assistant turn —
 * accumulate them into one bubble keyed by a synthetic id until
 * `assistant_message_persisted` swaps in the real BE-issued message id.
 */
const handleTextDelta: ChatEventHandler = (event, ctx) => {
  const payload = event as { text?: unknown };

  if (typeof payload.text !== 'string' || payload.text.length === 0) {
    return;
  }

  // Text resumes after a tool batch — close the open tool group so the
  // next tool_use spawns a new card below this text bubble.
  ctx.toolGroupMessageIdRef.current = null;

  // The streaming bubble (with its cursor) takes over as the "agent is
  // working" affordance.
  ctx.setIsThinking(false);

  const delta = payload.text;

  // Decide the target id synchronously so that subsequent events fired
  // in the same tick (`done`, `assistant_message_persisted`) observe
  // the ref before React flushes the updater. React may invoke
  // set-state updaters more than once (StrictMode, concurrent
  // rendering), so we keep the ref mutation out of the updater body.
  let targetID = ctx.streamingMessageIdRef.current;

  if (targetID === null) {
    targetID = `streaming-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    ctx.streamingMessageIdRef.current = targetID;
  }

  const resolvedID = targetID;

  ctx.setMessages((prev) => {
    const idx = prev.findIndex((m) => m.id === resolvedID);

    if (idx >= 0) {
      const next = prev.slice();

      next[idx] = {
        ...next[idx],
        text: next[idx].text + delta,
        isStreaming: true,
      };

      return next;
    }

    return [
      ...prev,
      {
        id: resolvedID,
        role: 'assistant',
        text: delta,
        isStreaming: true,
      },
    ];
  });
};

/**
 * BE emits `assistant_message_persisted` once the turn is committed to
 * storage. Swap the synthetic id for the real one and clear the
 * streaming flag so the bubble drops its cursor and gains feedback
 * affordances.
 */
const handleAssistantMessagePersisted: ChatEventHandler = (event, ctx) => {
  const payload = event as { messageID?: unknown };

  if (typeof payload.messageID !== 'string' || !payload.messageID) {
    return;
  }

  const { messageID } = payload;
  const streamingID = ctx.streamingMessageIdRef.current;

  ctx.setMessages((prev) => {
    if (streamingID !== null) {
      const idx = prev.findIndex((m) => m.id === streamingID);

      if (idx >= 0) {
        const next = prev.slice();

        next[idx] = {
          ...next[idx],
          id: messageID,
          messageID,
          isStreaming: false,
        };

        return next;
      }
    }

    return prev;
  });

  ctx.streamingMessageIdRef.current = null;
  ctx.toolGroupMessageIdRef.current = null;
};

/**
 * Flip any toolGroup rows still `pending`/`running` to a terminal
 * status. Called when a turn ends (`done` / `error`): no tool can
 * still be running past the turn's terminal event, but a dropped
 * `tool_result` (stream gap, replay eviction) would otherwise leave
 * the row stranded — and the collapsed ticker shimmering forever.
 * Returns `prev` untouched when nothing needs settling so React can
 * skip the re-render.
 */
function settleToolGroups(
  prev: ChatMessage[],
  finalStatus: 'complete' | 'error',
): ChatMessage[] {
  let changed = false;

  const next = prev.map((message) => {
    if (message.card?.kind !== 'toolGroup') return message;

    const hasOpenRows = message.card.tools.some(
      (t) => t.status === 'pending' || t.status === 'running',
    );

    if (!hasOpenRows) return message;

    changed = true;

    return {
      ...message,
      card: {
        ...message.card,
        tools: message.card.tools.map((t) =>
          t.status === 'pending' || t.status === 'running'
            ? { ...t, status: finalStatus }
            : t,
        ),
      },
    };
  });

  return changed ? next : prev;
}

/**
 * BE emits `done` when the turn is fully complete. Defensive: clear
 * streaming tracking in case assistant_message_persisted didn't fire
 * (e.g. a turn that produced no assistant text).
 */
const handleDone: ChatEventHandler = (_event, ctx) => {
  if (ctx.streamingMessageIdRef.current !== null) {
    const streamingID = ctx.streamingMessageIdRef.current;

    ctx.setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === streamingID);

      if (idx >= 0) {
        const next = prev.slice();

        next[idx] = { ...next[idx], isStreaming: false };

        return next;
      }

      return prev;
    });

    ctx.streamingMessageIdRef.current = null;
  }

  ctx.toolGroupMessageIdRef.current = null;
  ctx.setIsThinking(false);

  // The turn is over — no tool can still be running. Settle rows whose
  // tool_result never arrived so the ticker stops shimmering.
  ctx.setMessages((prev) => settleToolGroups(prev, 'complete'));

  // A gap was detected earlier in this turn (a reconnect that couldn't
  // recover everything) — now that the turn is over and fully
  // persisted, rebuild from history.
  ctx.consumeResyncAtTurnEnd();
};

/**
 * BE emits one `tool_use` per tool invocation. Rows accumulate into a
 * single ServerToolGroupCard until a text_delta /
 * assistant_message_persisted / done event closes the batch.
 */
const handleToolUse: ChatEventHandler = (event, ctx) => {
  const payload = event as { toolUseID?: unknown; toolName?: unknown };

  if (
    typeof payload.toolUseID !== 'string' ||
    typeof payload.toolName !== 'string'
  ) {
    return;
  }

  const { toolUseID, toolName } = payload;

  // Close any open streaming text bubble so the tool card sits below it
  // instead of merging into the same row.
  ctx.streamingMessageIdRef.current = null;

  // The tool card's running row takes over as the "agent is working"
  // affordance. (The dots were shown during the silent
  // argument-generation phase via `tool_use_generating`.)
  ctx.setIsThinking(false);

  let groupID = ctx.toolGroupMessageIdRef.current;

  if (groupID === null) {
    groupID = `toolgroup-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    ctx.toolGroupMessageIdRef.current = groupID;
  }

  const resolvedGroupID = groupID;

  const newItem = {
    id: toolUseID,
    label: toolDisplayName(toolName),
    status: 'running' as const,
  };

  ctx.setMessages((prev) => {
    const idx = prev.findIndex((m) => m.id === resolvedGroupID);

    if (idx >= 0) {
      const target = prev[idx];

      if (target.card?.kind !== 'toolGroup') return prev;

      const nextTools = [...target.card.tools, newItem];
      const next = prev.slice();

      next[idx] = {
        ...target,
        card: { ...target.card, tools: nextTools },
      };

      return next;
    }

    return [
      ...prev,
      {
        id: resolvedGroupID,
        role: 'assistant',
        text: '',
        card: {
          kind: 'toolGroup',
          tools: [newItem],
          controlled: true,
        },
      },
    ];
  });
};

/**
 * BE emits `tool_use_generating` when the model starts emitting a tool
 * call's JSON arguments — a silent stretch (no streamed text, no card
 * yet) that can run 20-30s for a large routine. Raise the thinking dots
 * so the agent doesn't look dead; the `tool_use` event lowers them when
 * the arguments are complete and the card appears.
 */
const handleToolUseGenerating: ChatEventHandler = (_event, ctx) => {
  ctx.setIsThinking(true);
};

/**
 * BE emits `tool_result` after each tool finishes. Find the toolGroup
 * card containing this toolUseID and flip the matching row's status.
 * Accept both camelCase (`toolUseId`, lowercase 'd') and snake_case
 * (`tool_use_id`) payload keys for forward-compat with any future
 * migration.
 */
const handleToolResult: ChatEventHandler = (event, ctx) => {
  const payload = event as {
    toolUseID?: unknown;
    toolUseId?: unknown;
    tool_use_id?: unknown;
    isError?: unknown;
    is_error?: unknown;
  };

  let toolUseID: string | null = null;

  if (typeof payload.toolUseID === 'string') {
    toolUseID = payload.toolUseID;
  } else if (typeof payload.toolUseId === 'string') {
    toolUseID = payload.toolUseId;
  } else if (typeof payload.tool_use_id === 'string') {
    toolUseID = payload.tool_use_id;
  }

  if (toolUseID === null) return;

  const useID = toolUseID;

  const errorFlag = payload.isError === true || payload.is_error === true;

  const nextStatus = errorFlag ? ('error' as const) : ('complete' as const);

  // A finished tool means the orchestrator is about to feed the result
  // back to the LLM — the next silent stretch. Re-raise the thinking
  // affordance until the next text_delta/tool_use. (Tools in a batch
  // dispatch sequentially, so if another tool is still running its card
  // row is also visible; the brief overlap is acceptable.)
  ctx.setIsThinking(true);

  ctx.setMessages((prev) => {
    const idx = prev.findIndex(
      (m) =>
        m.card?.kind === 'toolGroup' &&
        m.card.tools.some((t) => t.id === useID),
    );

    if (idx < 0) return prev;

    const target = prev[idx];

    if (target.card?.kind !== 'toolGroup') return prev;

    const nextTools = target.card.tools.map((t) =>
      t.id === useID ? { ...t, status: nextStatus } : t,
    );

    const next = prev.slice();

    next[idx] = {
      ...target,
      card: { ...target.card, tools: nextTools },
    };

    return next;
  });
};

/**
 * Legacy `assistant_text` event — kept for backward compatibility with
 * older BE builds and the ui-mock harness. No-op against the current
 * production BE.
 */
const handleAssistantText: ChatEventHandler = (event, ctx) => {
  const payload = event as { id?: unknown; text?: unknown };

  if (typeof payload.id !== 'string' || typeof payload.text !== 'string') {
    return;
  }

  const { id, text } = payload;

  ctx.setMessages((prev) => {
    const idx = prev.findIndex((m) => m.id === id);

    if (idx >= 0) {
      const next = prev.slice();

      next[idx] = { ...next[idx], text };

      return next;
    }

    return [...prev, { id, role: 'assistant', text }];
  });

  ctx.setIsThinking(false);
};

/**
 * BE emits `error` when a turn ends abnormally — the orchestrator's
 * terminal failure codes (token_budget, llm_unavailable, max_iterations,
 * …). The stream closes after this, so surface the server-provided
 * message as a banner the operator can act on (token_budget asks them
 * to start a new chat). Unlike `streamError`, it is NOT cleared on
 * every frame — no further frames arrive this turn, and the banner must
 * persist until the next send / new chat.
 */
const handleError: ChatEventHandler = (event, ctx) => {
  const payload = event as { code?: unknown; message?: unknown };

  const message =
    typeof payload.message === 'string' && payload.message.length > 0
      ? payload.message
      : 'The agent stopped unexpectedly.';

  const code = typeof payload.code === 'string' ? payload.code : 'unknown';

  // Mirror `done`: a turn that errored mid-stream may have left a
  // streaming bubble with a live cursor. Settle it so it doesn't spin
  // forever beneath the banner.
  const streamingID = ctx.streamingMessageIdRef.current;

  if (streamingID !== null) {
    ctx.setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === streamingID);

      if (idx < 0) return prev;

      const next = prev.slice();

      next[idx] = { ...next[idx], isStreaming: false };

      return next;
    });

    ctx.streamingMessageIdRef.current = null;
  }

  ctx.toolGroupMessageIdRef.current = null;

  // Rows whose tool_result never arrived didn't finish — the turn
  // died. Mark them errored (which also auto-expands the group so the
  // stall stays visible) instead of leaving the ticker shimmering
  // under the error banner.
  ctx.setMessages((prev) => settleToolGroups(prev, 'error'));

  ctx.setAgentError({ code, message });
  ctx.setIsThinking(false);

  // Same as `done`: an error is a terminal turn-exit, so a pending
  // gap-resync can safely rebuild from history now.
  ctx.consumeResyncAtTurnEnd();
};

const HANDLERS: Record<string, ChatEventHandler> = {
  user_message_persisted: handleUserMessagePersisted,
  text_delta: handleTextDelta,
  assistant_message_persisted: handleAssistantMessagePersisted,
  done: handleDone,
  tool_use: handleToolUse,
  tool_use_generating: handleToolUseGenerating,
  tool_result: handleToolResult,
  assistant_text: handleAssistantText,
  error: handleError,
};

/**
 * Events that prove a turn is running, so the flag goes up. Includes the
 * mid-turn bookkeeping frames (`assistant_message_persisted`) and the
 * legacy `assistant_text`, which say just as much about liveness as a
 * `text_delta` does. Raising on all of them is what lets a peer tab that
 * never sent the message learn a turn is in flight — it gets no fresh
 * snapshot, so its first signal is whichever frame happens to arrive
 * first.
 */
const TURN_PROGRESS_EVENTS = new Set<string>([
  'user_message_persisted',
  'text_delta',
  'assistant_message_persisted',
  'tool_use',
  'tool_use_generating',
  'tool_result',
  'assistant_text',
]);

/** Events that end a turn, so the flag comes down. */
const TURN_TERMINAL_EVENTS = new Set<string>(['done', 'error']);

/**
 * Route one stream event to its handler. Returns `true` when the event
 * type was recognized and handled; `false` when the caller should fall
 * through to its unknown-event hook (navigate frames, cards, mock-only
 * kinds).
 */
export function dispatchChatEvent(
  event: Record<string, unknown>,
  ctx: ChatEventContext,
): boolean {
  const { type } = event;

  if (typeof type !== 'string') return false;

  const handler = HANDLERS[type];

  if (handler === undefined) return false;

  // Turn-active is a function of the event's type alone, so it's decided
  // here off the two sets above rather than inside the handlers. Applied
  // before the handler runs: a terminal event's own cleanup can end in
  // the turn-end reseed, which reads the (synchronously mirrored) flag
  // and must see it already settled.
  if (TURN_PROGRESS_EVENTS.has(type)) {
    ctx.setIsTurnActive(true);
  } else if (TURN_TERMINAL_EVENTS.has(type)) {
    ctx.setIsTurnActive(false);
  }

  handler(event, ctx);

  return true;
}
